import { z } from 'zod';
import { dateStr, objectIdStr } from './common.js';
import { stationKey, type StationKey } from './station.js';
import { DEFAULT_MAX_HOURS_PER_DAY } from './job.js';

export const worker = z.object({
  _id: objectIdStr,
  name: z.string().min(1),
  stationKey,
  hoursPerDay: z.number().min(0).default(8),
  activeFrom: dateStr.optional(),
  activeTo: dateStr.optional(),
});
export type Worker = z.infer<typeof worker>;

export const workerWrite = worker.omit({ _id: true }).partial({ hoursPerDay: true });
export type WorkerWrite = z.infer<typeof workerWrite>;

export const EXCEPTION_KINDS = ['holiday', 'absence', 'extra'] as const;
export type ExceptionKind = (typeof EXCEPTION_KINDS)[number];
export const exceptionKind = z.enum(EXCEPTION_KINDS);

/**
 * Everything that deviates from "Mon–Fri, everyone works `hoursPerDay`".
 *
 * - `holiday` with no worker/station: the shop is shut, all capacity is 0.
 * - `absence`: subtract that worker's day (or `hours` if given).
 * - `extra`: add `hours` to a station (overtime, a weekend shift).
 */
export const calendarException = z.object({
  _id: objectIdStr,
  date: dateStr,
  kind: exceptionKind,
  workerId: objectIdStr.optional(),
  stationKey: stationKey.optional(),
  hours: z.number().optional(),
  note: z.string().optional(),
});
export type CalendarException = z.infer<typeof calendarException>;

export const calendarExceptionWrite = calendarException.omit({ _id: true });
export type CalendarExceptionWrite = z.infer<typeof calendarExceptionWrite>;

/** Replaces the JSON templates read off disk at request time. */
export const jobTemplate = z.object({
  _id: objectIdStr,
  name: z.string().min(1),
  routing: z.array(
    z.object({
      stationKey,
      hoursWork: z.number().min(0),
      bufferDays: z.number().int().min(0).default(1),
      /** Seeds the same cap on every batch made from this template. */
      maxHoursPerDay: z.number().positive().default(DEFAULT_MAX_HOURS_PER_DAY),
      /** Seeds the same dependency graph on every batch made from this template. */
      dependsOn: z.array(stationKey).optional(),
    }),
  ),
});
export type JobTemplate = z.infer<typeof jobTemplate>;

export const jobTemplateWrite = jobTemplate.omit({ _id: true });
export type JobTemplateWrite = z.infer<typeof jobTemplateWrite>;

/** The `medium` template, carried over from `sample_data/jobs_template-medium-01.json`. */
export const DEFAULT_TEMPLATES: {
  name: string;
  routing: { stationKey: StationKey; hoursWork: number; bufferDays: number; maxHoursPerDay?: number }[];
}[] = [
  {
    name: 'medium',
    routing: [
      { stationKey: 'ORDER_CREATION', hoursWork: 16, bufferDays: 1 },
      { stationKey: 'NESTING', hoursWork: 48, bufferDays: 1 },
      { stationKey: 'CNC', hoursWork: 16, bufferDays: 1 },
      { stationKey: 'CARPENTRY', hoursWork: 48, bufferDays: 1 },
      { stationKey: 'FINISHING_SAND', hoursWork: 32, bufferDays: 1 },
      { stationKey: 'FINISHING_PAINT', hoursWork: 16, bufferDays: 1 },
      { stationKey: 'ASSEMBLING', hoursWork: 16, bufferDays: 1 },
      { stationKey: 'DELIVERY', hoursWork: 16, bufferDays: 1 },
      { stationKey: 'INSTALLATION', hoursWork: 24, bufferDays: 0 },
    ],
  },
  {
    name: 'small',
    routing: [
      { stationKey: 'ORDER_CREATION', hoursWork: 8, bufferDays: 1 },
      { stationKey: 'NESTING', hoursWork: 16, bufferDays: 1 },
      { stationKey: 'CNC', hoursWork: 8, bufferDays: 1 },
      { stationKey: 'CARPENTRY', hoursWork: 24, bufferDays: 1 },
      { stationKey: 'FINISHING_SAND', hoursWork: 16, bufferDays: 1 },
      { stationKey: 'FINISHING_PAINT', hoursWork: 8, bufferDays: 1 },
      { stationKey: 'ASSEMBLING', hoursWork: 8, bufferDays: 1 },
      { stationKey: 'DELIVERY', hoursWork: 8, bufferDays: 1 },
      { stationKey: 'INSTALLATION', hoursWork: 16, bufferDays: 0 },
    ],
  },
];
